/**
 * 1NOW-1649 — what to show a renter for a pick-up / drop-off location.
 *
 * The field used to render `location.name` unconditionally. Most tenants name
 * their single location after the company, so renters saw "Pick-up location:
 * activationoio" — which tells them nothing about where to collect the car.
 * The address was on the record the whole time and went unused.
 */

const norm = (v?: string | null) => (v || "").trim();

/** Primary line: the name, unless it's just the company name repeated back. */
export const locationLabel = (location: any, companyName?: string): string => {
  const name = norm(location?.name);
  const address = norm(location?.address);
  if (!name) return address;
  if (!address) return name;
  if (name.toLowerCase() === norm(companyName).toLowerCase()) return address;
  return name;
};

/** Secondary line: the address, but only when it adds something. */
export const locationSubLabel = (
  location: any,
  companyName?: string,
): string => {
  const address = norm(location?.address);
  if (!address) return "";
  return locationLabel(location, companyName) === address ? "" : address;
};

/**
 * Both lines as one string, for surfaces that render a single value rather than
 * a stacked label + sub-label — e.g. the Booking Details summary on the vehicle
 * page, which is where the renter actually confirms and pays. That panel showed
 * the bare name, so the original complaint ("renter can't tell where to collect
 * the car") survived the first fix on the surface where it mattered most.
 */
export const locationDisplay = (location: any, companyName?: string): string => {
  const label = locationLabel(location, companyName);
  const sub = locationSubLabel(location, companyName);
  return sub ? `${label} · ${sub}` : label;
};
